import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Mail, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Motion";
import PageHeader from "../components/layout/PageHeader";
import { api } from "../lib/api";
import { business } from "../data/site";
import SEO from "../components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks for reaching out! We'll get back to you soon. A confirmation email is on its way to your inbox.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at " + business.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        route="contact"
        fallbackTitle="Contact Us | Shaan Curry House Ottawa"
        fallbackDescription="Reach Shaan Curry House on Merivale Road, Ottawa. Phone, WhatsApp, hours and directions."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact" },
        ]}
      />
      <PageHeader
        eyebrow="Get In Touch"
        title="Contact Us"
        subtitle="Questions, feedback or a reservation? We'd love to hear from you."
      />

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* FORM */}
          <Reveal>
            <h2 className="font-display font-semibold text-3xl text-white mb-6">Send us a message</h2>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Your Name *" testid="contact-name" value={form.name} onChange={update("name")} placeholder="Full name" />
                <Field label="Phone" testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="(613) 000-0000" />
              </div>
              <Field label="Email *" testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
              <Field label="Subject" testid="contact-subject" value={form.subject} onChange={update("subject")} placeholder="How can we help?" />
              <div>
                <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">Message *</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} placeholder="Write your message…"
                  className="w-full bg-[#141414] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600" />
              </div>
              <button type="submit" disabled={submitting} data-testid="contact-submit-btn"
                className="w-full bg-[#ffd100] text-[#0a0a0a] font-bold text-lg py-4 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60">
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>

          {/* INFO */}
          <Reveal delay={0.12}>
            <div className="bg-[#141414] rounded-2xl border border-white/10 p-8 space-y-7">
              <InfoRow icon={MapPin} title="Visit Us" lines={[business.addressLine1, business.addressLine2]} />
              <InfoRow icon={Clock} title="Opening Hours" lines={[business.hours]} />
              <InfoRow icon={Phone} title="Call Us" lines={[business.phone]} href={`tel:${business.phoneRaw}`} />
              <InfoRow icon={MessageCircle} title="WhatsApp" lines={[business.whatsapp]} href={`https://wa.me/${business.whatsappRaw}`} />
              <InfoRow icon={Mail} title="Email" lines={[business.email]} href={`mailto:${business.email}`} />
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 h-72">
              <iframe
                title="Shaan Curry House location"
                src="https://www.google.com/maps?q=1489+Merivale+Rd,+Nepean,+ON+K2E+5P3&output=embed"
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, testid, type = "text", ...rest }) => (
  <div>
    <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">{label}</label>
    <input type={type} data-testid={testid} {...rest}
      className="w-full bg-[#141414] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600" />
  </div>
);

const InfoRow = ({ icon: Icon, title, lines, href }) => {
  const content = (
    <div className="flex gap-4">
      <div className="w-12 h-12 grid place-items-center rounded-full bg-[#ffd100]/10 shrink-0">
        <Icon size={22} className="text-[#ffd100]" />
      </div>
      <div>
        <p className="font-display font-semibold text-white text-lg">{title}</p>
        {lines.map((l) => <p key={l} className="text-neutral-400 text-base">{l}</p>)}
      </div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" className="block hover:opacity-80 transition-opacity">{content}</a> : content;
};
